import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'goBackLabel'
	| 'modalTitle'
	| 'closeButton'
	| 'createdByLabel'
	| 'openChatLabel'
	| 'behaviourLabel'
	| 'instructionsLabel'
	| 'knowledgeLabel'
	| 'usersMetadataLabel'
	| 'reactionsMetadataLabel'
	| 'favouritesMetadataLabel'
	| 'betaLabel'
	| 'loadingAgentDetails'
	| 'agentNotFound'
	| 'scenariosLabel'
	| 'subagentsLabel';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		goBackLabel: {
			id: 'rovo-chat.view-agent.go-back-label',
			defaultMessage: 'Go back',
			description:
				'Button label for back navigation button in agent details modal. Returns user to the agent list view.',
		},
		modalTitle: {
			id: 'rovo-chat.view-agent.modal-title',
			defaultMessage: 'Browse agents',
			description: 'Title displayed at the top of the agent details modal/dialog window.',
		},
		closeButton: {
			id: 'rovo-chat.view-agent.close-button',
			defaultMessage: 'Close',
			description:
				'Button label for close button (typically X icon) to dismiss the agent details modal.',
		},
		createdByLabel: {
			id: 'rovo-chat.view-agent.created-by-label',
			defaultMessage: `<text>by</text> {agentName}`,
			description:
				'Text shown in agent details to attribute creator. Displays as "by [creator name]" where {agentName} is replaced with the creator\'s name.',
		},
		openChatLabel: {
			id: 'rovo-chat.view-agent.open-chat-label',
			defaultMessage: 'Open chat',
			description: 'Button label to open/start a chat conversation with the agent being viewed.',
		},
		behaviourLabel: {
			id: 'rovo-chat.view-agent.behaviour-label',
			defaultMessage: 'Behavior',
			description:
				"Section header label for the agent's behavior/personality settings in the agent details view.",
		},
		instructionsLabel: {
			id: 'rovo-chat.view-agent.instructions-label',
			defaultMessage: 'Instructions',
			description:
				"Section header label for the agent's custom instructions/system prompt in the agent details view.",
		},
		knowledgeLabel: {
			id: 'rovo-chat.view-agent.knowledge-label',
			defaultMessage: 'Knowledge',
			description:
				"Section header label for the agent's knowledge sources (Jira, Confluence, etc.) in the agent details view.",
		},
		usersMetadataLabel: {
			id: 'rovo-chat.view-agent.users-metadata-label',
			defaultMessage: '# of users',
			description:
				'Metadata label showing count of users who have interacted with the agent. Displayed in agent details stats section.',
		},
		reactionsMetadataLabel: {
			id: 'rovo-chat.view-agent.reactions-metadata-label',
			defaultMessage: '# of reactions',
			description:
				'Metadata label showing count of reactions/feedback given to the agent. Displayed in agent details stats section.',
		},
		favouritesMetadataLabel: {
			id: 'rovo-chat.view-agent.favourites-metadata-label',
			defaultMessage: '# of favourites',
			description:
				'Metadata label showing count of users who have favorited/starred the agent. Displayed in agent details stats section.',
		},
		betaLabel: {
			id: 'rovo-chat.view-agent.beta-label',
			defaultMessage: 'Beta',
			description:
				'Badge label indicating the agent is in beta/experimental stage. Displayed on agent details header.',
		},
		loadingAgentDetails: {
			id: 'rovo-chat.view-agent.loading-agent-details',
			defaultMessage: 'Loading agent details',
			description:
				'Accessible status message announced while the agent details view is loading in the browse agents modal.',
		},
		agentNotFound: {
			id: 'ai-mate.agent-selector.agent-not-found',
			defaultMessage: 'Agent not found',
			description:
				'Error message displayed when the requested agent cannot be found or no longer exists.',
		},
		scenariosLabel: {
			id: 'rovo-chat.view-agent.scenarios-label',
			defaultMessage: 'Scenarios',
			description:
				"Section header label for the agent's configured scenarios in the agent details view.",
		},
		subagentsLabel: {
			id: 'rovo-chat.view-agent.subagents-label',
			defaultMessage: 'Subagents',
			description:
				"Section header label for the agent's configured subagents in the agent details view.",
		},
	});

export default message;
